import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Input, 
  Select, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  Form, 
  message, 
  Tooltip, 
  Radio, 
  DatePicker,
  Checkbox,
  Typography,
  Divider
} from 'antd';
import { 
  Plus, 
  Search, 
  RotateCcw, 
  Info,
  Database,
  BarChart3,
  Zap,
  LayoutDashboard,
  Clock,
  User,
  Activity
} from 'lucide-react';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Text } = Typography;
const { RangePicker } = DatePicker;

interface MaterializationItem {
  key: string;
  name: string;
  strategy: 'detail' | 'summary' | 'result';
  owner: string;
  status: 'enabled' | 'disabled';
  createdAt: string;
  datasets: string[];
  indicators: string[];
  dimensions: string[];
  latestTable: string;
}

const strategyMap = {
  detail: '明细加速',
  summary: '汇总加速',
  result: '结果加速'
};

const mockData: MaterializationItem[] = [
  {
    key: '1',
    name: '热源运行数据物化方案',
    strategy: 'detail',
    owner: '张三',
    status: 'enabled',
    createdAt: '2024-03-20 10:00:00',
    datasets: ['ds_heat_source_raw', 'ds_station_meta'],
    indicators: ['供水温度', '回水温度', '瞬时流量'],
    dimensions: ['热源ID', '时间', '区域'],
    latestTable: 'mv_heat_source_detail_20240320'
  },
  {
    key: '2',
    name: '锅炉运行效率汇总方案',
    strategy: 'summary',
    owner: '李四',
    status: 'disabled',
    createdAt: '2024-03-21 14:30:00',
    datasets: ['ds_boiler_metrics'],
    indicators: ['热效率', '耗煤量', '产汽量'],
    dimensions: ['锅炉编号', '日期'],
    latestTable: 'mv_boiler_summary_daily'
  },
  {
    key: '3',
    name: '网口表运行指标结果加速',
    strategy: 'result',
    owner: '王五',
    status: 'enabled',
    createdAt: '2024-03-22 09:15:00',
    datasets: ['ds_network_meter_raw'],
    indicators: ['累计热量', '流量计读数'],
    dimensions: ['网口ID', '小时'],
    latestTable: 'mv_network_meter_result_h'
  }
];

interface MaterializationSchemeProps {
  onOpenConfig?: (type: string) => void;
}

export default function MaterializationScheme({ onOpenConfig }: MaterializationSchemeProps) {
  const [data, setData] = useState<MaterializationItem[]>(mockData);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  // Modal states
  const [manualExecVisible, setManualExecVisible] = useState(false);
  const [grayExecVisible, setGrayExecVisible] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [schemeSelectVisible, setSchemeSelectVisible] = useState(false);
  const [currentItem, setCurrentItem] = useState<MaterializationItem | null>(null);
  const [selectedScheme, setSelectedScheme] = useState<string | null>(null);

  // Gray execution states
  const [grayDateType, setGrayDateType] = useState('7');
  const [grayDates, setGrayDates] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  useEffect(() => {
    if (grayDateType === '7') {
      setGrayDates([dayjs().subtract(7, 'day'), dayjs()]);
    } else if (grayDateType === '30') {
      setGrayDates([dayjs().subtract(30, 'day'), dayjs()]);
    } else {
      setGrayDates(null);
    }
  }, [grayDateType]);

  const handleSearch = () => {
    message.success('查询成功');
  };

  const handleReset = () => {
    form.resetFields();
    handleSearch();
  };

  const toggleStatus = (record: MaterializationItem) => {
    const newStatus = record.status === 'enabled' ? 'disabled' : 'enabled';
    const newData = data.map(item => 
      item.key === record.key ? { ...item, status: newStatus as any } : item
    );
    setData(newData);
    message.success(`${newStatus === 'enabled' ? '启用' : '停用'}调度成功`);
  };

  const handleDelete = () => {
    if (currentItem) {
      setData(data.filter(item => item.key !== currentItem.key));
      setDeleteVisible(false);
      message.success('删除成功');
    }
  };

  const columns: ColumnsType<MaterializationItem> = [
    {
      title: '物化名称',
      dataIndex: 'name',
      key: 'name',
      // No width specified for auto-adaptation
      render: (text) => (
        <a className="text-blue-600 hover:underline font-medium" onClick={() => message.info('查看详情: ' + text)}>
          {text}
        </a>
      )
    },
    {
      title: '物化策略',
      dataIndex: 'strategy',
      key: 'strategy',
      width: 120,
      render: (val: keyof typeof strategyMap) => strategyMap[val]
    },
    {
      title: '负责人',
      dataIndex: 'owner',
      key: 'owner',
      width: 100,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 'enabled' ? 'success' : 'default'}>
          {status === 'enabled' ? '启用' : '停用'}
        </Tag>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 380,
      render: (_, record) => (
        <Space size={0} split={<Divider type="vertical" style={{ margin: '0 4px' }} />}>
          <Button type="link" size="small" className="px-1" onClick={() => message.info('编辑待更新')}>编辑</Button>
          
          <Button 
            type="link" 
            size="small" 
            className="px-1"
            onClick={() => toggleStatus(record)}
            danger={record.status === 'enabled'}
          >
            {record.status === 'enabled' ? '停用调度' : '启用调度'}
          </Button>

          <Button type="link" size="small" className="px-1" onClick={() => message.info('调度配置待更新')}>调度配置</Button>
          
          <Button type="link" size="small" className="px-1" onClick={() => { setCurrentItem(record); setManualExecVisible(true); }}>手动执行</Button>
          
          <Button type="link" size="small" className="px-1" onClick={() => { setCurrentItem(record); setGrayExecVisible(true); }}>灰度执行</Button>
          
          <Tooltip title={record.status === 'enabled' ? '启用状态不可删除' : ''}>
            <Button 
              type="link" 
              size="small" 
              danger 
              disabled={record.status === 'enabled'}
              className="px-1"
              onClick={() => { setCurrentItem(record); setDeleteVisible(true); }}
            >
              删除
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc] p-6 space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-end mb-2">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <LayoutDashboard className="text-blue-600" size={24} />
            物化方案管理
          </h2>
          <p className="text-slate-500 mt-1">通过预计算技术加速查询性能，支持明细、汇总及结果多级加速策略。</p>
        </div>
        <Button 
          type="primary" 
          size="large"
          icon={<Plus size={18} />} 
          className="h-11 px-6 shadow-md shadow-blue-100 rounded-lg flex items-center font-medium"
          onClick={() => setSchemeSelectVisible(true)}
        >
          新建物化方案
        </Button>
      </div>

      {/* Filter Area */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <Form form={form} layout="vertical" className="w-full">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <Form.Item name="name" label={<span className="text-slate-600 font-medium">物化名称</span>} className="mb-0">
              <Input placeholder="搜索方案名称" allowClear className="h-10 rounded-md" prefix={<Search size={14} className="text-slate-400" />} />
            </Form.Item>
            <Form.Item name="strategy" label={<span className="text-slate-600 font-medium">物化策略</span>} className="mb-0">
              <Select placeholder="选择加速策略" className="h-10 rounded-md" allowClear>
                <Select.Option value="detail">明细加速</Select.Option>
                <Select.Option value="summary">汇总加速</Select.Option>
                <Select.Option value="result">结果加速</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="owner" label={<span className="text-slate-600 font-medium">负责人</span>} className="mb-0">
              <Input placeholder="搜索负责人" allowClear className="h-10 rounded-md" prefix={<User size={14} className="text-slate-400" />} />
            </Form.Item>
            <div className="flex items-end space-x-3">
              <Button type="primary" className="h-10 w-24 rounded-md font-medium" onClick={handleSearch}>查询</Button>
              <Button icon={<RotateCcw size={16} />} className="h-10 w-24 rounded-md text-slate-500 flex items-center justify-center" onClick={handleReset}>重置</Button>
            </div>
          </div>
        </Form>
      </div>

      {/* Table Area */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-bottom border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-2">
            <Activity size={18} className="text-blue-500" />
            <span className="font-bold text-slate-700">方案列表</span>
          </div>
          <div className="text-slate-400 text-xs">
            共 {data.length} 个物化方案
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: 1200 }}
          className="custom-table"
          expandable={{
            expandedRowRender: (record) => (
              <div className="bg-slate-50 p-6 rounded-lg m-4 border border-slate-100 shadow-inner">
                <div className="grid grid-cols-2 gap-y-4 gap-x-8">
                  <div className="flex flex-col gap-1">
                    <Text type="secondary" className="text-[11px] uppercase tracking-wider font-bold">关联数据集</Text>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {record.datasets.map(ds => <Tag key={ds} className="m-0 bg-white border-slate-200 text-slate-600">{ds}</Tag>)}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Text type="secondary" className="text-[11px] uppercase tracking-wider font-bold">关联指标</Text>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {record.indicators.map(ind => <Tag key={ind} color="blue" className="m-0">{ind}</Tag>)}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Text type="secondary" className="text-[11px] uppercase tracking-wider font-bold">关联维度</Text>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {record.dimensions.map(dim => <Tag key={dim} color="cyan" className="m-0">{dim}</Tag>)}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Text type="secondary" className="text-[11px] uppercase tracking-wider font-bold">最新物化表</Text>
                    <div className="mt-1 flex items-center gap-2">
                      <code className="bg-slate-800 text-slate-200 px-2 py-1 rounded text-xs font-mono">{record.latestTable}</code>
                      <Button type="link" size="small" className="p-0 text-[10px]">复制表名</Button>
                    </div>
                  </div>
                </div>
              </div>
            ),
          }}
          pagination={{ 
            pageSize: 10, 
            showSizeChanger: true, 
            showTotal: (total) => `共 ${total} 条`,
            className: "px-6 py-4"
          }}
        />
      </div>

      {/* Manual Execution Modal */}
      <Modal
        title="确认手动执行"
        open={manualExecVisible}
        onOk={() => { setManualExecVisible(false); message.success('全量计算已触发'); }}
        onCancel={() => setManualExecVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <div className="flex items-start space-x-3 py-4">
          <Info className="text-blue-500 mt-1" size={20} />
          <p className="text-gray-600">确认立即触发一次全量计算？会清空并重建整张物化表。</p>
        </div>
      </Modal>

      {/* Gray Execution Modal */}
      <Modal
        title="灰度执行配置"
        open={grayExecVisible}
        width={600}
        onOk={() => { setGrayExecVisible(false); message.success('灰度试算已提交'); }}
        onCancel={() => setGrayExecVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <div className="space-y-4 py-2">
          <div className="bg-blue-50 p-3 rounded border border-blue-100 text-blue-800 text-xs leading-relaxed">
            灰度执行是手动触发下的一种安全试算模式，仅对最近 N 天或指定小范围时间窗口执行物化，用于快速验证配置正确性、数据合理性、执行性能，只对当前这次手动灰度执行生效，执行完即失效，不影响配置、不影响后续调度，避免全量执行带来的计算资源浪费与数据错误风险。
          </div>
          
          <Form layout="vertical">
            <Form.Item label="配置日期范围" required>
              <Radio.Group 
                value={grayDateType} 
                onChange={(e) => setGrayDateType(e.target.value)}
                className="mb-3"
              >
                <Radio.Button value="7">近 7 天</Radio.Button>
                <Radio.Button value="30">近 30 天</Radio.Button>
                <Radio.Button value="custom">自定义</Radio.Button>
              </Radio.Group>
              <div className="mt-2">
                <RangePicker 
                  style={{ width: '100%' }} 
                  value={grayDates}
                  onChange={(dates) => setGrayDates(dates as any)}
                  disabled={grayDateType !== 'custom'}
                />
              </div>
            </Form.Item>
          </Form>
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        title="确认删除"
        open={deleteVisible}
        onOk={handleDelete}
        onCancel={() => setDeleteVisible(false)}
        okButtonProps={{ danger: true }}
        okText="确定删除"
        cancelText="取消"
      >
        <div className="space-y-4 py-4">
          <div className="flex items-start space-x-3">
            <Info className="text-red-500 mt-1" size={20} />
            <p className="text-gray-600 font-medium">确认删除物化方案，删除后方案不可恢复？</p>
          </div>
          <div className="pl-8">
            <Checkbox defaultChecked>同步删除物化表</Checkbox>
          </div>
        </div>
      </Modal>

      {/* Scheme Selection Modal */}
      <Modal
        title={<div className="text-xl font-bold py-2">新建物化方案</div>}
        open={schemeSelectVisible}
        width={900}
        centered
        onOk={() => {
          if (!selectedScheme) {
            message.warning('请选择一种物化方案');
            return;
          }
          setSchemeSelectVisible(false);
          message.success(`已选择 ${strategyMap[selectedScheme as keyof typeof strategyMap]}，正在进入配置页面...`);
          onOpenConfig?.(selectedScheme);
        }}
        onCancel={() => {
          setSchemeSelectVisible(false);
          setSelectedScheme(null);
        }}
        okText="下一步"
        cancelText="取消"
        okButtonProps={{ size: 'large', className: 'px-8 rounded-lg h-11' }}
        cancelButtonProps={{ size: 'large', className: 'rounded-lg h-11' }}
      >
        <div className="py-8">
          <div className="flex gap-6 justify-center">
            {/* Detail Acceleration */}
            <div 
              className={`relative flex flex-col items-center p-8 border-2 rounded-2xl cursor-pointer transition-all duration-300 w-1/3 group ${
                selectedScheme === 'detail' 
                  ? 'border-blue-500 bg-blue-50/50 shadow-lg shadow-blue-100' 
                  : 'border-slate-100 bg-white hover:border-slate-200 hover:shadow-md'
              }`}
              onClick={() => setSelectedScheme('detail')}
            >
              <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 ${
                selectedScheme === 'detail' ? 'bg-blue-500 text-white shadow-lg shadow-blue-200' : 'bg-blue-50 text-blue-500'
              }`}>
                <Database size={36} />
              </div>
              <Tag className="mb-3 px-3 py-0.5 rounded-full bg-blue-100 text-blue-600 border-none text-[10px] font-bold uppercase tracking-wider">
                基础加速
              </Tag>
              <h4 className={`text-xl font-bold mb-3 ${selectedScheme === 'detail' ? 'text-blue-700' : 'text-slate-800'}`}>
                明细加速
              </h4>
              <p className="text-slate-500 text-xs text-center leading-relaxed px-2">
                核心是多表预关联生成宽表、为聚合打基础，适用于多表即席查询、汇总加速数据源及数据溯源排查场景。
              </p>
              {selectedScheme === 'detail' && (
                <div className="absolute top-4 right-4 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white">
                  <Plus size={14} className="rotate-45" />
                </div>
              )}
            </div>

            {/* Summary Acceleration */}
            <div 
              className={`relative flex flex-col items-center p-8 border-2 rounded-2xl cursor-pointer transition-all duration-300 w-1/3 group ${
                selectedScheme === 'summary' 
                  ? 'border-emerald-500 bg-emerald-50/50 shadow-lg shadow-emerald-100' 
                  : 'border-slate-100 bg-white hover:border-slate-200 hover:shadow-md'
              }`}
              onClick={() => setSelectedScheme('summary')}
            >
              <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 ${
                selectedScheme === 'summary' ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-200' : 'bg-emerald-50 text-emerald-500'
              }`}>
                <BarChart3 size={36} />
              </div>
              <Tag className="mb-3 px-3 py-0.5 rounded-full bg-emerald-100 text-emerald-600 border-none text-[10px] font-bold uppercase tracking-wider">
                多维聚合
              </Tag>
              <h4 className={`text-xl font-bold mb-3 ${selectedScheme === 'summary' ? 'text-emerald-700' : 'text-slate-800'}`}>
                汇总加速
              </h4>
              <p className="text-slate-500 text-xs text-center leading-relaxed px-2">
                核心是细粒度预聚合、支持灵活多维分析及上卷/下钻，适用于BI自助分析及核心指标高频查询场景。
              </p>
              {selectedScheme === 'summary' && (
                <div className="absolute top-4 right-4 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-white">
                  <Plus size={14} className="rotate-45" />
                </div>
              )}
            </div>

            {/* Result Acceleration */}
            <div 
              className={`relative flex flex-col items-center p-8 border-2 rounded-2xl cursor-pointer transition-all duration-300 w-1/3 group ${
                selectedScheme === 'result' 
                  ? 'border-violet-500 bg-violet-50/50 shadow-lg shadow-violet-100' 
                  : 'border-slate-100 bg-white hover:border-slate-200 hover:shadow-md'
              }`}
              onClick={() => setSelectedScheme('result')}
            >
              <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 ${
                selectedScheme === 'result' ? 'bg-violet-500 text-white shadow-lg shadow-violet-200' : 'bg-violet-50 text-violet-500'
              }`}>
                <Zap size={36} />
              </div>
              <Tag className="mb-3 px-3 py-0.5 rounded-full bg-violet-100 text-violet-600 border-none text-[10px] font-bold uppercase tracking-wider">
                极致性能
              </Tag>
              <h4 className={`text-xl font-bold mb-3 ${selectedScheme === 'result' ? 'text-violet-700' : 'text-slate-800'}`}>
                结果加速
              </h4>
              <p className="text-slate-500 text-xs text-center leading-relaxed px-2">
                核心是固定粒度快照、满足高并发与固定报表极致性能需求，适用于高管大屏及数百指标并发查询场景。
              </p>
              {selectedScheme === 'result' && (
                <div className="absolute top-4 right-4 w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center text-white">
                  <Plus size={14} className="rotate-45" />
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
